import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { SiAppstore } from 'react-icons/si';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  shouldShowLandingAppDownloadBar,
  markLandingAppDownloadBarDismissed
} from '../../utils/appDownloadPromoStorage';
import './LandingAppDownloadBar.css';

function GooglePlayMark({ size = 24 }) {
  return (
    <svg className="landing-app-bar__gp" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path fill="#00D9FF" d="M11.128 12.003 1.09 22.011a2.73 2.73 0 0 0 3.999 1.62l11.29-6.377-5.251-5.25Z" />
      <path fill="#00F076" d="m21.338 9.67-4.899-2.806-5.311 5.14 5.251 5.25 4.944-2.865a2.663 2.663 0 0 0 0-4.719h.015Z" />
      <path fill="#FFD23F" d="M1.09 1.995c-.06.225-.091.457-.09.69V21.32c0 .233.03.465.09.69l10.038-10.007L1.09 1.995Z" />
      <path fill="#FF1744" d="m11.128 12.003 5.311-5.139L5.126.383A2.79 2.79 0 0 0 3.723 0 2.73 2.73 0 0 0 1.09 1.988l10.038 10.015Z" />
    </svg>
  );
}

/**
 * Fixed bottom strip on the public landing page: App Store + Google Play, shown on load
 * until dismissed (hidden for several days via localStorage).
 */
const LandingAppDownloadBar = ({ onBottomInsetChange }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (shouldShowLandingAppDownloadBar()) {
      setOpen(true);
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      onBottomInsetChange?.(0);
      return;
    }
    const node = rootRef.current;
    if (!node || !onBottomInsetChange) return;

    const apply = () => {
      onBottomInsetChange(node.offsetHeight);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(node);
    return () => {
      ro.disconnect();
      onBottomInsetChange(0);
    };
  }, [open, onBottomInsetChange]);

  const dismiss = useCallback(() => {
    markLandingAppDownloadBarDismissed();
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  if (!open) return null;

  const bar = (
    <div ref={rootRef} className="landing-app-bar" role="region" aria-label={t('landingAppBarTitle')}>
      <div className="landing-app-bar__inner" dir={isRTL ? 'rtl' : 'ltr'}>
        <button type="button" className="landing-app-bar__close" onClick={dismiss} aria-label={t('close')}>
          <X size={16} strokeWidth={2.5} />
        </button>

        <div className="landing-app-bar__text">
          <h2 className="landing-app-bar__title">{t('landingAppBarTitle')}</h2>
          <p className="landing-app-bar__subtitle">{t('landingAppBarSubtitle')}</p>
        </div>

        <div className="landing-app-bar__stores">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="landing-app-bar__store landing-app-bar__store--ios"
            aria-label={`${t('appStoreBadgeKicker')} ${t('appStoreBadgeTitle')}`}
          >
            <span className="landing-app-bar__storeIcon">
              <SiAppstore className="landing-app-bar__as" aria-hidden />
            </span>
            <span className="landing-app-bar__storeText">
              <span className="landing-app-bar__storeKicker">{t('appStoreBadgeKicker')}</span>
              <span className="landing-app-bar__storeName">{t('appStoreBadgeTitle')}</span>
            </span>
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="landing-app-bar__store landing-app-bar__store--android"
            aria-label={`${t('googlePlayBadgeKicker')} ${t('googlePlayBadgeTitle')}`}
          >
            <span className="landing-app-bar__storeIcon">
              <GooglePlayMark size={22} />
            </span>
            <span className="landing-app-bar__storeText">
              <span className="landing-app-bar__storeKicker">{t('googlePlayBadgeKicker')}</span>
              <span className="landing-app-bar__storeName">{t('googlePlayBadgeTitle')}</span>
            </span>
          </a>
        </div>

        <button type="button" className="landing-app-bar__notNow" onClick={dismiss}>
          {t('landingAppBarNotNow')}
        </button>
      </div>
    </div>
  );

  return createPortal(bar, document.body);
};

export default LandingAppDownloadBar;
